import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { CircularProgress } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import HistoryIcon from '@mui/icons-material/History'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddTaskIcon from '@mui/icons-material/AddTask'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { triggerAudit, listAudits, getAudit, exportAuditPdf } from 'services/portfolioService'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { toast } from 'react-toastify'
import ReactMarkdown from 'react-markdown'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'

const SEVERITY_ITEM = {
  critical: { label: 'Critical', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  high:     { label: 'High',     color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
  medium:   { label: 'Medium',   color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  low:      { label: 'Low',      color: '#15803d', bg: '#dcfce7', border: '#bbf7d0' },
}

const SEVERITY_BUILDING = {
  severe:    { label: 'Severe',    color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  moderate:  { label: 'Moderate',  color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  contained: { label: 'Contained', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
}

const SEVERITY_OVERVIEW = {
  high:   { label: 'High risk',    color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  medium: { label: 'Medium risk',  color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  low:    { label: 'Low risk',     color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
}

function parseNarrative(raw) {
  if (!raw || typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const parsed = JSON.parse(trimmed)
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.buildings)) return parsed
  } catch (_) {}
  return null
}

const GAP_DESCRIPTIONS = {
  'Fire Risk Assessment': 'Legal requirement for all non-domestic buildings under the Regulatory Reform (Fire Safety) Order 2005',
  'Gas Safety Certificate (CP12)': 'Annual gas safety check required by the Gas Safety (Installation and Use) Regulations 1998',
  'EICR': 'Electrical Installation Condition Report. Required every 5 years for rented properties',
  'EPC': 'Energy Performance Certificate. Required when selling or letting, valid for 10 years',
  'Legionella Risk Assessment': 'Required under HSE L8 guidance to manage legionella risk in water systems',
  'Asbestos Survey': 'Required under the Control of Asbestos Regulations 2012 for non-domestic buildings',
  'Lift Inspection (LOLER)': 'Thorough examination required every 6-12 months under LOLER 1998',
  'Insurance Certificate': 'Buildings insurance and public liability. Typically required by mortgage/lease',
  'Fire Alarm Certificate': 'Annual testing and certification to BS 5839 standards',
  'Emergency Lighting': 'Annual testing and certification required under BS 5266',
}

const AUDIT_PHASES = [
  'Collecting documents and metadata…',
  'Checking compliance and renewals…',
  'Detecting duplicates and misfiled items…',
  'Composing your information risk assessment…',
]

import { hideEmail } from 'utils/user'

const AuditTab = ({ portfolioId, auditSchedule, bpCount = 0, ownerName, ownerEmail, isOwner }) => {
  const isScheduled = auditSchedule === 'weekly' || auditSchedule === 'monthly'

  const [audits, setAudits] = useState([])
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [insightFile, setInsightFile] = useState(null)
  const [feedback, setFeedback] = useState(null) // 'up' | 'down' | null
  const [phaseIndex, setPhaseIndex] = useState(0)
  const hasAutoTriggered = useRef(false)

  const fetchAudits = useCallback(async () => {
    try {
      const res = await listAudits(portfolioId)
      const list = res.data.data || []
      setAudits(list)
      const pending = list.find(a => a.status === 'pending' || a.status === 'processing')
      setRunning(!!pending)
      // Keep loading=true until the latest completed audit is hydrated, otherwise
      // there's a brief window where the empty state flashes before the report paints.
      const latest = list.find(a => a.status === 'completed')
      if (latest) {
        try {
          const r = await getAudit(portfolioId, latest.id)
          setSelectedAudit(r.data.data)
        } catch (_) {}
      }
    } catch (_) {
    } finally {
      setLoading(false)
    }
  }, [portfolioId])

  useEffect(() => { fetchAudits() }, [fetchAudits])

  useEffect(() => {
    if (!running) return
    const intervalId = setInterval(() => {
      listAudits(portfolioId).then(res => {
        const list = res.data.data || []
        setAudits(list)
        const pending = list.find(a => a.status === 'pending' || a.status === 'processing')
        if (!pending) {
          setRunning(false)
          const completed = list.find(a => a.status === 'completed')
          if (completed) {
            getAudit(portfolioId, completed.id)
              .then(r => setSelectedAudit(r.data.data))
              .catch(() => {})
          }
        }
      })
    }, 5000)
    return () => clearInterval(intervalId)
  }, [running, portfolioId])

  const handleTrigger = () => {
    setRunning(true)
    triggerAudit(portfolioId)
      .then(() => {
        const ownerLabel = ownerName
          ? (ownerEmail ? `${ownerName} (${hideEmail(ownerEmail)})` : ownerName)
          : (ownerEmail ? hideEmail(ownerEmail) : 'the portfolio owner')
        const recipient = isOwner
          ? (ownerEmail ? `you (${hideEmail(ownerEmail)})` : 'you')
          : `the portfolio owner, ${ownerLabel}`
        toast.success(`Audit started. The report will be emailed to ${recipient} when ready.`)
      })
      .catch(err => {
        setRunning(false)
        toast.error(err.response?.data?.error || 'Failed to start audit')
      })
  }

  const handleSelectAudit = (auditId) => {
    getAudit(portfolioId, auditId)
      .then(r => setSelectedAudit(r.data.data))
      .catch(() => toast.error('Failed to load audit'))
  }

  const [exportingPdf, setExportingPdf] = useState(false)
  const handleExportPdf = async () => {
    if (!selectedAudit || selectedAudit.status !== 'completed' || exportingPdf) return
    setExportingPdf(true)
    try {
      const result = await exportAuditPdf(portfolioId, selectedAudit.id)
      const blob = new Blob([result.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const disposition = result.headers?.['content-disposition'] || ''
      const match = disposition.match(/filename="?([^"]+)"?/i)
      link.download = match ? match[1] : `information-risk-assessment-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast.error('Failed to export PDF')
    } finally {
      setExportingPdf(false)
    }
  }

  const handleFileClick = (file) => {
    setInsightFile({
      id: file.fileId,
      displayName: file.name || file.fileName,
      buildingPassportId: file.buildingPassportId,
      buildingPassportName: file.building,
    })
  }

  if (loading) return <LoadingState><CircularProgress size={28} sx={{ color: '#D24724' }} /></LoadingState>

  const report = selectedAudit?.reportData
  const narrativeData = parseNarrative(selectedAudit?.aiNarrative)

  const recipientLabel = isOwner
    ? (ownerEmail ? <>you (<strong>{hideEmail(ownerEmail)}</strong>)</> : 'you')
    : ownerName
      ? <>the portfolio owner, <strong>{ownerName}</strong>{ownerEmail ? <> ({hideEmail(ownerEmail)})</> : null}</>
      : ownerEmail
        ? <>the portfolio owner (<strong>{hideEmail(ownerEmail)}</strong>)</>
        : 'the portfolio owner'

  return (
    <Container>
      <RecipientNote>
        Audit reports are emailed to {recipientLabel} when an audit completes.
      </RecipientNote>
      <TopBar>
        {isScheduled ? (
          <ScheduledChip title={`Automated ${auditSchedule} audits are on. Next run will happen automatically`}>
            <CheckCircleIcon style={{ fontSize: 16 }} />
            Automated {auditSchedule} audits on{running ? '. Running now' : ''}
          </ScheduledChip>
        ) : (
          <RunButton onClick={handleTrigger} disabled={running}>
            {running ? <><CircularProgress size={14} sx={{ color: 'white' }} /> Running audit...</> : <><PlayArrowIcon style={{ fontSize: 18 }} /> Run Audit</>}
          </RunButton>
        )}
        {audits.length > 0 && (
          <AuditHistory>
            <HistoryIcon style={{ fontSize: 14, color: '#999' }} />
            {audits.slice(0, 5).map(a => (
              <HistoryChip key={a.id} $active={selectedAudit?.id === a.id} $status={a.status}
                onClick={() => a.status === 'completed' && handleSelectAudit(a.id)}>
                {a.createdAt?.split(' ')[0]}{a.status === 'processing' && ' ...'}
              </HistoryChip>
            ))}
          </AuditHistory>
        )}
      </TopBar>

      {!selectedAudit && !running && (
        <EmptyState>
          <EmptyIcon><PlayArrowIcon style={{ fontSize: 28 }} /></EmptyIcon>
          <EmptyTitle>No audits yet</EmptyTitle>
          <EmptyText>Run an audit to get a full compliance report with AI-powered insights, compliance gap analysis, and renewal tracking across your portfolio.</EmptyText>
        </EmptyState>
      )}

      {running && !selectedAudit && (
        <RunningState><CircularProgress size={24} sx={{ color: '#D24724' }} /><span>Auditing your portfolio... This may take a minute.</span></RunningState>
      )}

      {selectedAudit && report && (
        <Report>
          <ReportMetaRow>
            <ReportMeta>
              Audit completed {selectedAudit.completedAt}
              {report.totals?.portfolioBuildings > selectedAudit.bpCount
                ? `. Audited ${selectedAudit.bpCount} of ${report.totals.portfolioBuildings} Building Passports`
                : `: ${selectedAudit.bpCount} Building Passports`}
              , {selectedAudit.fileCount} files
            </ReportMeta>
            {selectedAudit.status === 'completed' && (
              <ExportPdfButton onClick={handleExportPdf} disabled={exportingPdf} title="Download Information Risk Assessment as PDF">
                {exportingPdf
                  ? <CircularProgress size={14} sx={{ color: '#D24724' }} />
                  : <PictureAsPdfIcon style={{ fontSize: 16 }} />
                }
                <span>{exportingPdf ? 'Exporting…' : 'Export PDF'}</span>
              </ExportPdfButton>
            )}
          </ReportMetaRow>

          {report.totals && (
            <SummaryStrip>
              <Chip>{report.totals.files?.toLocaleString()} documents</Chip>
              {report.totals.expired > 0 && <Chip $warn>{report.totals.expired} overdue</Chip>}
              {report.totals.expiringSoon > 0 && <Chip $caution>{report.totals.expiringSoon} expiring 90d</Chip>}
              {report.totals.misfiled > 0 && <Chip $warn>{report.totals.misfiled} misfiled</Chip>}
            </SummaryStrip>
          )}

          {(selectedAudit.aiNarrative || selectedAudit.aiSummary) && (
            <Section>
              <SectionHead>Information Risk Assessment</SectionHead>
              <SummaryCard>
                {narrativeData
                  ? <StructuredNarrative data={narrativeData} buildings={report.buildings || []} />
                  : <NarrativeMarkdown><ReactMarkdown>{selectedAudit.aiNarrative || selectedAudit.aiSummary}</ReactMarkdown></NarrativeMarkdown>
                }
                <FeedbackRow>
                  <FeedbackLabel>Was this assessment helpful?</FeedbackLabel>
                  <FeedbackBtn $active={feedback === 'up'} onClick={() => { setFeedback('up'); toast.success('Thanks for your feedback!') }}>
                    <ThumbUpIcon style={{ fontSize: 16 }} />
                  </FeedbackBtn>
                  <FeedbackBtn $active={feedback === 'down'} $negative onClick={() => { setFeedback('down'); toast.info('Thanks. We\'ll improve future audits') }}>
                    <ThumbDownIcon style={{ fontSize: 16 }} />
                  </FeedbackBtn>
                </FeedbackRow>
              </SummaryCard>
            </Section>
          )}

          {report.buildings?.length > 0 && (
            <Section>
              <SectionHead>Building Passport Overview</SectionHead>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <Th>Building Passport</Th>
                      <Th $right>Files</Th>
                      <Th $right>SP</Th>
                      <Th $right>Manual</Th>
                      <Th $right>Dup Extras</Th>
                      <Th $right>Misfiled</Th>
                      <Th $right>Overdue</Th>
                      <Th $right>Expiring</Th>
                      <Th $right>Renewed</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.buildings.map((bp, i) => (
                      <tr key={i}>
                        <Td><BldgName>{bp.name}</BldgName><BldgAddr>{bp.address}</BldgAddr></Td>
                        <Td $right>{bp.fileCount}</Td>
                        <Td $right>{bp.spCount || 0}</Td>
                        <Td $right>{bp.manualCount || 0}</Td>
                        <Td $right>{bp.duplicateExtras > 0 ? <Caution>{bp.duplicateExtras}</Caution> : '0'}</Td>
                        <Td $right>{bp.misfiledCount > 0 ? <Warn>{bp.misfiledCount}</Warn> : '0'}</Td>
                        <Td $right>{bp.expiredCount > 0 ? <Warn>{bp.expiredCount}</Warn> : '0'}</Td>
                        <Td $right>{bp.expiringSoonCount > 0 ? <Caution>{bp.expiringSoonCount}</Caution> : '0'}</Td>
                        <Td $right>{bp.renewedCount > 0 ? <Ok>{bp.renewedCount}</Ok> : '0'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            </Section>
          )}

          {report.complianceGaps?.length > 0 && (
            <Section>
              <SectionHead><ErrorOutlineIcon style={{ fontSize: 16, color: '#dc2626' }} /> Compliance Gaps</SectionHead>
              <GapDescription>Key UK compliance document types checked against each Building Passport. Missing types may indicate compliance risk.</GapDescription>
              <GapList>
                {report.complianceGaps.map((gap, idx) => (
                  <GapRow key={idx}>
                    <GapBuilding>{gap.building}</GapBuilding>
                    {gap.missing?.length > 0 ? (
                      <GapMissing>
                        {gap.missing.map((m, i) => (
                          <GapChip key={i} title={GAP_DESCRIPTIONS[m] || ''}>{m}</GapChip>
                        ))}
                      </GapMissing>
                    ) : (
                      <GapOk><CheckCircleIcon style={{ fontSize: 14 }} /> All key types present</GapOk>
                    )}
                  </GapRow>
                ))}
              </GapList>
            </Section>
          )}

          {report.expired?.length > 0 && (
            <Section>
              <SectionHead><WarningAmberIcon style={{ fontSize: 16, color: '#dc2626' }} /> Documents Overdue for Renewal ({report.expired.length})</SectionHead>
              <FileList>
                {report.expired.map((f, i) => (
                  <FileRow key={i} onClick={() => handleFileClick(f)}>
                    <FileName>{f.name}</FileName>
                    <FileMeta>{f.type}: {f.building}. Expired {f.date} ({f.source})</FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}

          {report.expiringSoon?.length > 0 && (
            <Section>
              <SectionHead>Expiring Within 90 Days ({report.expiringSoon.length})</SectionHead>
              <FileList>
                {report.expiringSoon.map((f, i) => (
                  <FileRow key={i} onClick={() => handleFileClick(f)}>
                    <FileName>{f.name}</FileName>
                    <FileMeta>{f.type}: {f.building}. Expires {f.date}</FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}

          {report.misfiled?.length > 0 && (
            <Section>
              <SectionHead><ErrorOutlineIcon style={{ fontSize: 16, color: '#dc2626' }} /> Potentially Misfiled Documents ({report.misfiled.length})</SectionHead>
              <GapDescription>These documents appear to belong to a different Building Passport in your portfolio. Click a row to inspect.</GapDescription>
              <FileList>
                {report.misfiled.map((f, i) => (
                  <FileRow key={i} onClick={() => handleFileClick(f)}>
                    <FileName>{f.fileName}</FileName>
                    <FileMeta>{f.building} → suggested: <strong>{f.suggestedBuilding}</strong>: {f.reason}</FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}

          {report.renewed?.length > 0 && (
            <Section>
              <SectionHead><CheckCircleIcon style={{ fontSize: 16, color: '#059669' }} /> Successfully Renewed ({report.renewed.length})</SectionHead>
              <RenewedNote>{report.renewed.length} documents have been renewed and no longer require action.</RenewedNote>
              <FileList style={{ marginTop: 8 }}>
                {report.renewed.map((f, i) => (
                  <FileRow key={i} onClick={() => handleFileClick(f)}>
                    <FileNameOk>{f.name}</FileNameOk>
                    <FileMeta>{f.type}: {f.building}. Expired {f.date} (renewed)</FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}

          {report.assets?.length > 0 && (
            <Section>
              <SectionHead><PrecisionManufacturingIcon style={{ fontSize: 16 }} /> Building Assets ({report.assets.length})</SectionHead>
              <AssetGrid>
                {report.assets.map((a, i) => (
                  <AssetItem key={i}>
                    <AssetName>{a.name}</AssetName>
                    <AssetMeta>{a.assetType}: {a.building}{a.location ? `: ${a.location}` : ''}: {a.fileCount} doc{a.fileCount !== 1 ? 's' : ''}</AssetMeta>
                  </AssetItem>
                ))}
              </AssetGrid>
            </Section>
          )}
        </Report>
      )}

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </Container>
  )
}

const StructuredNarrative = ({ data, buildings: reportBuildings = [] }) => {
  const buildings = data.buildings || []
  const recommendations = [...(data.recommendations || [])].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))
  const overviewSev = SEVERITY_OVERVIEW[data.overviewSeverity] || SEVERITY_OVERVIEW.medium

  // Map building name → id for converting recommendations into deep-linked tasks.
  const bpIdByName = useMemo(() => {
    const map = {}
    reportBuildings.forEach(b => { if (b.name && b.id) map[b.name] = b.id })
    return map
  }, [reportBuildings])

  const [expanded, setExpanded] = useState(() => {
    // Auto-expand buildings that are severe; keep contained/moderate collapsed by default.
    const set = {}
    buildings.forEach(b => { if (b.severity === 'severe') set[b.name] = true })
    return set
  })
  const [openTargetPicker, setOpenTargetPicker] = useState(null) // recommendation index when multi-target

  const toggle = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }))

  const createTaskForBuilding = (rec, buildingName) => {
    const bpId = bpIdByName[buildingName]
    if (!bpId) {
      toast.error(`Can't find "${buildingName}" in this portfolio.`)
      return
    }
    const description = [rec.reason, buildingName && `Target: ${buildingName}`, 'Source: portfolio audit recommendation']
      .filter(Boolean).join('\n\n')
    const qs = new URLSearchParams({
      title: rec.action,
      description,
      bpId,
      source: 'audit',
    }).toString()
    redirect(`${PRIVATE_PATHS.createTask}?${qs}`)
  }

  const handleRecClick = (rec, idx) => {
    const targets = rec.targetBuildings?.filter(t => bpIdByName[t]) || []
    if (targets.length === 0) {
      toast.info('This recommendation has no targetable Building Passports in your portfolio.')
      return
    }
    if (targets.length === 1) {
      createTaskForBuilding(rec, targets[0])
      return
    }
    setOpenTargetPicker(openTargetPicker === idx ? null : idx)
  }

  return (
    <div>
      {data.overview && (
        <OverviewCallout $bg={overviewSev.bg} $border={overviewSev.border} $color={overviewSev.color}>
          <OverviewLabel>{overviewSev.label}</OverviewLabel>
          <OverviewText>{data.overview}</OverviewText>
        </OverviewCallout>
      )}

      {buildings.length > 0 && (
        <BuildingList>
          {buildings.map((b, idx) => {
            const sev = SEVERITY_BUILDING[b.severity] || SEVERITY_BUILDING.moderate
            const isOpen = !!expanded[b.name]
            const itemCount = (b.items || []).length
            return (
              <BuildingCard key={idx} $border={sev.border}>
                <BuildingHeader onClick={() => toggle(b.name)} $hover>
                  <BuildingHeaderLeft>
                    {isOpen ? <ExpandMoreIcon style={{ fontSize: 18, color: '#666' }} /> : <ChevronRightIcon style={{ fontSize: 18, color: '#666' }} />}
                    {bpIdByName[b.name] ? (
                      <BuildingName
                        as="button"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          redirect(`/bp-list/${bpIdByName[b.name]}`)
                        }}
                        title="Open this Building Passport"
                      >
                        {b.name}
                      </BuildingName>
                    ) : (
                      <BuildingName>{b.name}</BuildingName>
                    )}
                    <SeverityBadge $color={sev.color} $bg={sev.bg} $border={sev.border}>{sev.label}</SeverityBadge>
                  </BuildingHeaderLeft>
                  <BuildingMeta>{itemCount === 0 ? 'No gaps' : `${itemCount} gap${itemCount > 1 ? 's' : ''}`}</BuildingMeta>
                </BuildingHeader>
                <BuildingSummary>{b.summary}</BuildingSummary>
                {isOpen && itemCount > 0 && (
                  <ItemList>
                    {b.items.map((it, i) => {
                      const isev = SEVERITY_ITEM[it.severity] || SEVERITY_ITEM.medium
                      return (
                        <Item key={i}>
                          <ItemHead>
                            <ItemTitle>{it.requirement}</ItemTitle>
                            <SeverityBadge $small $color={isev.color} $bg={isev.bg} $border={isev.border}>{isev.label}</SeverityBadge>
                          </ItemHead>
                          <ItemRisk>{it.risk}</ItemRisk>
                          {it.regulationRef && it.regulationRef !== 'N/A' && (
                            <ItemReg>{it.regulationRef}</ItemReg>
                          )}
                        </Item>
                      )
                    })}
                  </ItemList>
                )}
              </BuildingCard>
            )
          })}
        </BuildingList>
      )}

      {recommendations.length > 0 && (
        <RecommendationsBlock>
          <RecommendationsHead>Recommended actions</RecommendationsHead>
          <RecommendationsList>
            {recommendations.map((r, i) => {
              const targets = r.targetBuildings?.filter(t => bpIdByName[t]) || []
              const canCreate = targets.length > 0
              return (
                <Recommendation key={i}>
                  <RecPriority>{r.priority}</RecPriority>
                  <RecBody>
                    <RecAction>{r.action}</RecAction>
                    <RecReason>{r.reason}</RecReason>
                    {r.targetBuildings?.length > 0 && (
                      <RecTargets>
                        {r.targetBuildings.map((t, j) => <RecTarget key={j}>{t}</RecTarget>)}
                      </RecTargets>
                    )}
                    {openTargetPicker === i && targets.length > 1 && (
                      <TargetPicker>
                        <TargetPickerLabel>Create task for which Building Passport?</TargetPickerLabel>
                        {targets.map((t, j) => (
                          <TargetPickerOption key={j} onClick={() => { setOpenTargetPicker(null); createTaskForBuilding(r, t) }}>
                            {t}
                          </TargetPickerOption>
                        ))}
                      </TargetPicker>
                    )}
                  </RecBody>
                  {canCreate && (
                    <CreateTaskBtn onClick={() => handleRecClick(r, i)} title="Create task from this recommendation">
                      <AddTaskIcon style={{ fontSize: 16 }} />
                      <span>{targets.length > 1 ? 'Create task…' : 'Create task'}</span>
                    </CreateTaskBtn>
                  )}
                </Recommendation>
              )
            })}
          </RecommendationsList>
        </RecommendationsBlock>
      )}
    </div>
  )
}

export default AuditTab

const Container = styled.div``

const RecipientNote = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #475569;
  margin-bottom: 16px;
  & strong { color: #0f172a; font-weight: 600; }
`
const LoadingState = styled.div` display: flex; justify-content: center; padding: 48px; `

const TopBar = styled.div` display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; `

const RunButton = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 18px;
  background: ${({ theme }) => theme.colors.primary}; color: white; border: none; border-radius: 6px;
  font-size: 13px; font-weight: 500; cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.9; } &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const ScheduledChip = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  font-size: 12px; font-weight: 600;
  text-transform: capitalize;
`

const ReportMetaRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-bottom: 12px; flex-wrap: wrap;
`

const ExportPdfButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
  font-size: 12px; font-weight: 500; cursor: pointer;
  flex-shrink: 0;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.colors.primary}; color: ${({ theme }) => theme.colors.primary}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const AuditHistory = styled.div` display: flex; align-items: center; gap: 6px; flex-wrap: wrap; `

const HistoryChip = styled.button`
  padding: 4px 10px; font-size: 11px; border-radius: 4px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : (theme.colors.grey?.[500] || '#e6e6e6')};
  background: ${({ $active }) => $active ? '#fbede9' : 'white'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.haiti};
  cursor: ${({ $status }) => $status === 'completed' ? 'pointer' : 'default'};
  font-weight: ${({ $active }) => $active ? 600 : 400};
`

const EmptyState = styled.div` display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 50px 24px; text-align: center; `
const EmptyIcon = styled.div` width: 56px; height: 56px; border-radius: 14px; background: #fbede9; display: flex; align-items: center; justify-content: center; color: ${({ theme }) => theme.colors.primary}; `
const EmptyTitle = styled.h3` font-size: 16px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; margin: 0; `
const EmptyText = styled.p` font-size: 13px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin: 0; max-width: 420px; line-height: 1.5; `
const RunningState = styled.div` display: flex; align-items: center; gap: 12px; justify-content: center; padding: 40px; font-size: 14px; color: ${({ theme }) => theme.colors.haiti}; `

const Report = styled.div``
const ReportMeta = styled.div` font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-bottom: 16px; `

const Section = styled.div` margin-bottom: 20px; `
const SectionHead = styled.h3`
  font-size: 14px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; margin: 0 0 10px;
  display: flex; align-items: center; gap: 6px;
  &:before { content: ''; display: inline-block; width: 3px; height: 14px; background: ${({ theme }) => theme.colors.primary}; border-radius: 2px; }
`

const SummaryCard = styled.div` background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; padding: 16px; `
const SummaryText = styled.div` font-size: 14px; line-height: 1.6; color: ${({ theme }) => theme.colors.haiti}; white-space: pre-wrap; `

const NarrativeMarkdown = styled.div`
  font-size: 14px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.haiti};
  h2 { font-size: 15px; font-weight: 600; margin: 18px 0 8px; color: ${({ theme }) => theme.colors.haiti}; }
  h2:first-child { margin-top: 0; }
  h3 { font-size: 14px; font-weight: 600; margin: 12px 0 6px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 0 0 10px 20px; padding: 0; }
  li { margin-bottom: 4px; }
  strong { font-weight: 600; }
  em { font-style: italic; }
  code { background: #f4f4f5; padding: 1px 5px; border-radius: 3px; font-size: 13px; }
`

const SummaryStrip = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  margin: -8px 0 16px;
`

const Chip = styled.span`
  font-size: 11px; font-weight: 500;
  padding: 4px 10px; border-radius: 12px;
  background: ${({ $warn, $caution }) => $warn ? '#fef2f2' : ($caution ? '#fffbeb' : '#f4f4f5')};
  color: ${({ $warn, $caution, theme }) => $warn ? '#dc2626' : ($caution ? '#b45309' : theme.colors.haiti)};
  border: 1px solid ${({ $warn, $caution }) => $warn ? '#fecaca' : ($caution ? '#fde68a' : 'transparent')};
`

const TableScroll = styled.div` overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 8px; `

const Table = styled.table` width: 100%; border-collapse: collapse; background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden; `
const Th = styled.th`
  padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; text-align: ${({ $right }) => $right ? 'right' : 'left'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'};
`
const Td = styled.td` padding: 12px 14px; font-size: 13px; color: ${({ theme }) => theme.colors.haiti}; text-align: ${({ $right }) => $right ? 'right' : 'left'}; border-bottom: 1px solid #f2f2f2; `
const BldgName = styled.div` font-weight: 500; `
const BldgAddr = styled.div` font-size: 11px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; `
const Warn = styled.span` color: #dc2626; font-weight: 600; `
const Caution = styled.span` color: #f59e0b; font-weight: 600; `
const Ok = styled.span` color: #059669; font-weight: 600; `

const GapList = styled.div` background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden; `
const GapRow = styled.div` padding: 12px 14px; &:not(:last-child) { border-bottom: 1px solid #f2f2f2; } `
const GapBuilding = styled.div` font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; margin-bottom: 6px; `
const GapMissing = styled.div` display: flex; flex-wrap: wrap; gap: 4px; `
const GapChip = styled.span` font-size: 11px; padding: 2px 8px; background: #fef2f2; color: #dc2626; border-radius: 4px; font-weight: 500; `
const GapOk = styled.div` display: flex; align-items: center; gap: 4px; font-size: 12px; color: #059669; font-weight: 500; `

const FileList = styled.div` background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden; max-height: 400px; overflow-y: auto; `
const FileRow = styled.div`
  padding: 10px 14px; cursor: pointer;
  &:not(:last-child) { border-bottom: 1px solid #f2f2f2; }
  &:hover { background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'}; }
`
const FileName = styled.div` font-size: 13px; font-weight: 500; color: ${({ theme }) => theme.colors.primary}; `
const FileMeta = styled.div` font-size: 11px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-top: 2px; `

const RenewedNote = styled.div` font-size: 13px; color: #059669; padding: 12px 14px; background: #ecfdf5; border-radius: 8px; `

const AssetGrid = styled.div` background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden; `
const AssetItem = styled.div` padding: 10px 14px; &:not(:last-child) { border-bottom: 1px solid #f2f2f2; } `
const AssetName = styled.div` font-size: 13px; font-weight: 500; color: ${({ theme }) => theme.colors.haiti}; `
const AssetMeta = styled.div` font-size: 11px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-top: 2px; `
const FileNameOk = styled.div` font-size: 13px; font-weight: 500; color: #059669; `

const GapDescription = styled.p`
  font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin: -6px 0 10px; line-height: 1.4;
`

const FeedbackRow = styled.div`
  display: flex; align-items: center; gap: 8px; margin-top: 14px; padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
`

const FeedbackLabel = styled.span` font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; `

const FeedbackBtn = styled.button`
  background: ${({ $active, $negative }) => $active ? ($negative ? '#fef2f2' : '#ecfdf5') : 'none'};
  color: ${({ $active, $negative, theme }) => $active ? ($negative ? '#dc2626' : '#059669') : (theme.colors.grey?.[700] || '#b3b3b3')};
  border: 1px solid ${({ $active }) => $active ? 'currentColor' : 'transparent'};
  border-radius: 6px; padding: 4px 8px; cursor: pointer; display: flex; align-items: center;
  &:hover { color: ${({ $negative }) => $negative ? '#dc2626' : '#059669'}; }
`

// ── Structured narrative ──
const OverviewCallout = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  padding: 12px 14px; margin-bottom: 14px;
  border: 1px solid ${({ $border }) => $border};
  background: ${({ $bg }) => $bg};
  border-radius: 8px;
`
const OverviewLabel = styled.div`
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`
const OverviewText = styled.div` font-size: 14px; line-height: 1.55; color: ${({ theme }) => theme.colors.haiti}; `

const BuildingList = styled.div` display: flex; flex-direction: column; gap: 10px; `
const BuildingCard = styled.div`
  border: 1px solid ${({ $border }) => $border};
  border-radius: 8px;
  background: white;
  overflow: hidden;
`
const BuildingHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  ${({ $hover }) => $hover && css`&:hover { background: #fafafa; }`}
`
const BuildingHeaderLeft = styled.div` display: flex; align-items: center; gap: 8px; min-width: 0; `
const BuildingName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  background: transparent;
  border: none;
  padding: 0;
  font-family: inherit;
  text-align: left;

  /* When rendered as a <button> via the as= prop it stays a link-ish click target. */
  &[type="button"] {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: transparent;
    text-underline-offset: 3px;
    transition: text-decoration-color 0.12s;
  }
  &[type="button"]:hover {
    text-decoration-color: ${({ theme }) => theme.colors.primary};
  }
`
const BuildingMeta = styled.div` font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; flex-shrink: 0; `
const BuildingSummary = styled.div`
  font-size: 13px; line-height: 1.5; color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  padding: 0 14px 12px; margin-top: -4px;
`

const SeverityBadge = styled.span`
  display: inline-flex; align-items: center;
  font-size: ${({ $small }) => $small ? '10px' : '11px'};
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px;
  padding: ${({ $small }) => $small ? '2px 6px' : '3px 8px'};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
`

const ItemList = styled.div` display: flex; flex-direction: column; border-top: 1px solid #f2f2f2; `
const Item = styled.div`
  padding: 10px 14px;
  &:not(:last-child) { border-bottom: 1px solid #f6f6f6; }
`
const ItemHead = styled.div` display: flex; align-items: center; gap: 8px; margin-bottom: 4px; flex-wrap: wrap; `
const ItemTitle = styled.div` font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; `
const ItemRisk = styled.div` font-size: 13px; line-height: 1.5; color: ${({ theme }) => theme.colors.haiti}; `
const ItemReg = styled.div`
  font-size: 11px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 4px; font-style: italic;
`

const RecommendationsBlock = styled.div` margin-top: 18px; padding-top: 14px; border-top: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; `
const RecommendationsHead = styled.h4`
  font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  margin: 0 0 10px;
`
const RecommendationsList = styled.ol` list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; `
const Recommendation = styled.li`
  display: flex; gap: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 6px;
  align-items: flex-start;
`

const CreateTaskBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 6px;
  font-size: 12px; font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

const TargetPicker = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
  display: flex; flex-direction: column; gap: 4px;
`
const TargetPickerLabel = styled.div`
  font-size: 11px; color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  padding: 2px 4px 6px;
`
const TargetPickerOption = styled.button`
  text-align: left;
  padding: 6px 10px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  &:hover { background: #f4f4f5; }
`
const RecPriority = styled.div`
  flex-shrink: 0;
  width: 24px; height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
`
const RecBody = styled.div` display: flex; flex-direction: column; gap: 4px; min-width: 0; `
const RecAction = styled.div` font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; `
const RecReason = styled.div` font-size: 12px; line-height: 1.5; color: ${({ theme }) => theme.colors.grey?.[800] || '#666'}; `
const RecTargets = styled.div` display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; `
const RecTarget = styled.span`
  font-size: 10px; padding: 2px 6px;
  background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 3px;
  color: ${({ theme }) => theme.colors.haiti};
`
